import { FC } from "react";
import {
  Avatar,
  Card,
  CardContent,
  CardHeader,
  SxProps,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  ChallengeInProgress,
  useSelectedChallengeContext,
} from "@context/SelectedChallengeContext";
import { Cancel, ThumbDown, ThumbUp } from "@mui/icons-material";
import { Challenger, TeamReference } from "@/models/pdfData";
import { gameState } from "@/services/gameState";
import { otherTeam } from "@/pdf/utils";
import {
  CompletedChallenge,
  SamsChallengeOutcome,
  SuccessfulChallenge,
} from "@/models/gameState";

const ChallengeResultSelector: FC = () => {
  const { challenge, setChallengeResult } = useSelectedChallengeContext();
  const { team1, team2 } = gameState("teams");

  const toggleButtonSx: SxProps = {
    display: "flex",
    gap: 1,
  };
  const orientation = useMediaQuery("(min-width:1200px)")
    ? "horizontal"
    : "vertical";

  const SuccessfulToggleButton: FC<{ team: TeamReference }> = ({ team }) => (
    <ToggleButton value={team} sx={toggleButtonSx}>
      <ThumbUp />
      Erfolgreich
    </ToggleButton>
  );

  const UnsuccessfulToggleButton: FC<{ team: TeamReference }> = ({ team }) => (
    <ToggleButton value={team} sx={toggleButtonSx}>
      <ThumbDown />
      Nicht erfolgreich
    </ToggleButton>
  );

  const Team1ToggleButton = () => (
    <ToggleButton value={"team1"} sx={toggleButtonSx}>
      <Avatar src={team1.logoImageMax400x400.url} />
      {team1.shortName}
    </ToggleButton>
  );

  const Team2ToggleButton = () => (
    <ToggleButton value={"team2"} sx={toggleButtonSx}>
      <Avatar src={team2.logoImageMax400x400.url} />
      {team2.shortName}
    </ToggleButton>
  );

  return (
    <Card
      variant="elevation"
      elevation={5}
      sx={{ width: orientation === "vertical" ? "350px" : "800px", mx: 2 }}
    >
      <CardHeader
        title={"Ergebnis"}
        titleTypographyProps={{ variant: "subtitle1", align: "center" }}
        sx={{ pb: 0 }}
      />
      <CardContent sx={{ justifyContent: "center", display: "flex" }}>
        {challenge.challenger ? (
          <ToggleButtonGroup
            orientation={orientation}
            color="primary"
            value={getChallengeResult(challenge)}
            exclusive
            onChange={(event, value) => {
              setChallengeResult(value);
            }}
            sx={{ px: 2 }}
          >
            {challenge.challenger === "referee"
              ? [
                  <Team1ToggleButton key="ref-team1" />,
                  <Team2ToggleButton key="ref-team2" />,
                ]
              : [
                  <SuccessfulToggleButton
                    key="team-success"
                    team={challenge.challenger}
                  />,
                  <UnsuccessfulToggleButton
                    key="team-fail"
                    team={otherTeam(challenge.challenger)}
                  />,
                ]}
            <ToggleButton value={"unresolvable"} sx={toggleButtonSx}>
              <Cancel />
              Nicht auflösbar
            </ToggleButton>
          </ToggleButtonGroup>
        ) : (
          <Typography>Antragsteller unbekannt</Typography>
        )}
      </CardContent>
    </Card>
  );
};

const getChallengeResult = (
  challenge: ChallengeInProgress,
): TeamReference | "unresolvable" | undefined => {
  const { successful, unresolved, challenger } = challenge;
  if (challenger === "referee") {
    if ((challenge as SuccessfulChallenge).outcome === "POINT_TEAM1") {
      return "team1";
    } else if ((challenge as SuccessfulChallenge).outcome === "POINT_TEAM2") {
      return "team2";
    } else {
      return "unresolvable";
    }
  } else if (unresolved) {
    return "unresolvable";
  } else if (!challenger) {
    return undefined;
  } else {
    return successful ? challenger : otherTeam(challenger);
  }
};

export default ChallengeResultSelector;
